/**
 * Claim Agent for Ameen Insurance Assistant
 * 
 * Handles the FIRST STAGE of the claim filing flow:
 * - Policy Verification
 * - Transition to Vehicle Inspection
 * 
 * THIS IS THE SINGLE SOURCE OF TRUTH FOR CLAIM AGENT TOOLS.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender, UIStateManager } from '../types';
import { noEntryRequirements } from '../guardrails';
import { logger } from '../../utils/logger';
import { handleSearchFAQ } from './RouterAgent';

/**
 * Create Claim Agent node
 */
export function createClaimAgent(voiceGender: VoiceGender): AgentNode {
  return {
    id: 'claim_agent',
    name: 'Claim Agent',
    description: 'Handles policy verification for claim filing',

    entryGuardrails: noEntryRequirements(),

    exitGuardrails: {
      transitions: new Map([
        ['go_to_policy', {
          target: { agentId: 'policy_agent' },
          guardrails: []
        }],
        ['go_to_inspection', {
          target: { agentId: 'vehicle_inspection_agent' },
          guardrails: []
        }],
        ['go_to_router', {
          target: { agentId: 'router_agent' },
          guardrails: []
        }]
      ]),
      fallback: { agentId: 'router_agent' }
    },

    initialPrompt: 'ابدأ المطالبة.',

    /**
     * Called when this agent is activated
     */
    onActivate: async (context: AgentContext) => {
      logger.info('[ClaimAgent] Activated');
      // No complex state needed here anymore as submission is moved
    },

    /**
     * Handle tool calls
     */
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext,
      uiState?: UIStateManager
    ): Promise<ToolCallResult> => {
      logger.tool('[ClaimAgent] Tool called:', toolName, args);

      switch (toolName) {
        // ═══ ROUTING TOOLS ═══
        case 'go_to_router_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Returning to menu' },
            triggersTransition: true,
            transitionTo: 'router_agent',
            transitionDelayMs: -1,
            transitionData: { return_to_menu: true },
          };

        case 'go_to_policy_agent':
          return {
            toolName,
            args,
            result: { success: true, message: 'Switching to policy flow' },
            triggersTransition: true,
            transitionTo: 'policy_agent',
            transitionDelayMs: -1
          };

        case 'go_to_vehicle_inspection_agent':
          logger.info('[ClaimAgent] Transitioning to vehicle_inspection_agent');
          return {
            toolName,
            args,
            result: { success: true, message: 'Starting vehicle inspection' },
            triggersTransition: true,
            transitionTo: 'vehicle_inspection_agent',
            transitionDelayMs: -1,
            transitionData: {
              caller_agent: 'claim_agent',
              flow_state: args.flow_state || {}
            }
          };

        // ═══ UI TOOLS ═══
        case 'request_policy_inquiry':
          if (uiState) {
            uiState.setFlowStep('claim_policy_inquiry');
          }
          return {
            toolName,
            args,
            result: {
              success: true,
              message: "Policy inquiry popup displayed. Waiting for [system_ui] signal."
            }
          };

        // ═══ FAQ TOOL ═══
        case 'search_faq':
          return await handleSearchFAQ(args, toolName);

        default:
          logger.warn('[ClaimAgent] Unknown tool:', toolName);
          return {
            toolName,
            args,
            result: { success: false, error: `Unknown tool: ${toolName}` }
          };
      }
    },

    onDeactivate: async (context: AgentContext) => {
      logger.info('[ClaimAgent] Deactivated');
    }
  };
}
